import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { admin } from './admin-api';

// RBAC (spec 12.3): assign a named owner to an engine or intervention. Ownership drives who
// may dispose its alerts (an owner, or executive+). Data, not hardcoded.

export default function Owners() {
  const qc = useQueryClient();
  const owners = useQuery({ queryKey: ['admin', 'owners'], queryFn: admin.owners });
  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: admin.users });
  const [scopeType, setScopeType] = useState<'engine' | 'intervention'>('engine');
  const [scopeId, setScopeId] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const assign = useMutation({
    mutationFn: () => admin.setOwner(scopeType, scopeId.trim(), ownerId),
    onSuccess: () => { setErr(null); setScopeId(''); qc.invalidateQueries({ queryKey: ['admin', 'owners'] }); },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed to assign owner'),
  });
  const remove = useMutation({
    mutationFn: ({ scope_type, scope_id }: { scope_type: string; scope_id: string }) => admin.removeOwner(scope_type, scope_id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'owners'] }),
  });

  const emailFor = (id: string) => users.data?.find((u) => u.id === id)?.email ?? id;

  return (
    <div>
      <div className="ph">Admin · Engine / Intervention Owners</div>
      <div className="h2">Ownership & disposition rights</div>
      <p className="lead">An owner (or an executive / superadmin) may dispose the alerts on their engine or intervention.</p>

      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="qhead"><span className="qdot" style={{ background: 'var(--amber)' }} />Assign owner</div>
        {err && <div className="login-err">{err}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
          <div>
            <label className="small faint" style={{ display: 'block', marginBottom: 6, fontSize: 12, textTransform: 'uppercase' }}>Scope</label>
            <select className="c2-input" value={scopeType} onChange={(e) => setScopeType(e.target.value as 'engine' | 'intervention')}>
              <option value="engine">engine</option>
              <option value="intervention">intervention</option>
            </select>
          </div>
          <div>
            <label className="small faint" style={{ display: 'block', marginBottom: 6, fontSize: 12, textTransform: 'uppercase' }}>Id</label>
            <input className="c2-input" value={scopeId} onChange={(e) => setScopeId(e.target.value)} placeholder={scopeType === 'engine' ? 'e.g. 2' : 'e.g. O-02'} />
          </div>
          <div>
            <label className="small faint" style={{ display: 'block', marginBottom: 6, fontSize: 12, textTransform: 'uppercase' }}>Owner</label>
            <select className="c2-input" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
              <option value="">select a user…</option>
              {users.data?.map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
            </select>
          </div>
          <button type="button" className="btn primary" disabled={!scopeId.trim() || !ownerId || assign.isPending} onClick={() => assign.mutate()}>
            {assign.isPending ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>

      <div className="panel">
        <div className="qhead"><span className="qdot" style={{ background: 'var(--teal)' }} />Current owners</div>
        <table>
          <thead>
            <tr><th>Scope</th><th>Id</th><th>Owner</th><th>Assigned</th><th className="center">Actions</th></tr>
          </thead>
          <tbody>
            {owners.data?.map((o) => (
              <tr key={`${o.scope_type}/${o.scope_id}`}>
                <td><span className="tag muted">{o.scope_type}</span></td>
                <td className="mono small">{o.scope_id}</td>
                <td className="small">{emailFor(o.owner_user_id)}</td>
                <td className="small faint">{new Date(o.assigned_at).toLocaleDateString()}</td>
                <td className="center">
                  <button type="button" className="btn ghost" style={{ minHeight: 'auto', padding: '5px 10px', fontSize: 12 }} onClick={() => remove.mutate({ scope_type: o.scope_type, scope_id: o.scope_id })}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {!owners.data?.length && <tr><td colSpan={5} className="small faint">No owners assigned yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
